import type { Role } from "@scs/contracts";
import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes } from "react-router";
import { ApiClient } from "./api/client.js";
import type { AuthSession } from "./api/client.js";
import { ProtectedRoute } from "./components/protected-route.js";
import { AdminUsersPage } from "./pages/admin-users-page.js";
import { ConversationsPage } from "./pages/conversations-page.js";
import { GovernancePage } from "./pages/governance-page.js";
import { LoginPage } from "./pages/login-page.js";
import { SessionPage } from "./pages/session-page.js";

const tenantRoles: readonly Role[] = [
  "ENTERPRISE_ADMIN",
  "SUPERVISOR",
  "COLLECTOR",
  "AUDITOR"
];
const sessionStorageKey = "scs.auth.session.v1";
const roleLabels: Record<Role, string> = {
  PLATFORM_ADMIN: "平台管理员",
  ENTERPRISE_ADMIN: "管理员",
  SUPERVISOR: "主管",
  COLLECTOR: "催收员",
  AUDITOR: "审计员"
};

export function CurrentAccount(props: { user: AuthSession["user"] }): React.JSX.Element {
  return <div aria-label="当前登录账号" className="app-current-account">
    <strong className="app-current-account__email">{props.user.email}</strong>
    <span className="app-current-account__role">{roleLabels[props.user.role]} · {props.user.role}</span>
  </div>;
}

type StoredSession = AuthSession & { expiresAt: number };

function loadStoredSession(): AuthSession | null {
  try {
    const value = window.localStorage.getItem(sessionStorageKey);
    if (value === null) return null;
    const session = JSON.parse(value) as Partial<StoredSession>;
    if (
      typeof session.expiresAt !== "number" ||
      session.expiresAt <= Date.now() ||
      typeof session.sessionToken !== "string" ||
      typeof session.expiresInSeconds !== "number" ||
      session.user === undefined
    ) {
      window.localStorage.removeItem(sessionStorageKey);
      return null;
    }
    return {
      expiresInSeconds: session.expiresInSeconds,
      sessionToken: session.sessionToken,
      user: session.user
    };
  } catch {
    window.localStorage.removeItem(sessionStorageKey);
    return null;
  }
}

function storeSession(session: AuthSession): void {
  const stored: StoredSession = {
    ...session,
    expiresAt: Date.now() + session.expiresInSeconds * 1_000
  };
  window.localStorage.setItem(sessionStorageKey, JSON.stringify(stored));
}

export function App(props: { api?: ApiClient }): React.JSX.Element {
  const api = useMemo(() => props.api ?? new ApiClient(), [props.api]);
  return (
    <BrowserRouter>
      <AppRoutes api={api} initialSession={api.getStoredSession()} />
    </BrowserRouter>
  );
}

export function SyntheticSmokeModeBanner(props: { enabled: boolean }): React.JSX.Element | null {
  return props.enabled ? <div aria-live="polite" className="smoke-mode-banner" role="status">
    合成验收模式：AI 为本地确定性模拟，Gateway 只投递合成事件；WhatsApp 未连接、不会真实发送。
  </div> : null;
}

export function AppRoutes(props: {
  api: ApiClient;
  initialSession?: AuthSession | null;
}): React.JSX.Element {
  const [session, setSession] = useState<AuthSession | null>(
    props.initialSession ?? loadStoredSession()
  );

  useEffect(() => {
    props.api.setUnauthorizedHandler(() => {
      window.localStorage.removeItem(sessionStorageKey);
      setSession(null);
    });
    return () => { props.api.setUnauthorizedHandler(null); };
  }, [props.api]);

  useEffect(() => {
    let timer: number | undefined;
    const applyThemeForLocalTime = (): void => {
      const hour = new Date().getHours();
      document.documentElement.dataset["scsTheme"] = hour >= 7 && hour < 19 ? "day" : "night";
      const nextBoundary = new Date();
      nextBoundary.setHours(hour >= 7 && hour < 19 ? 19 : 7, 0, 5, 0);
      if (nextBoundary.getTime() <= Date.now()) nextBoundary.setDate(nextBoundary.getDate() + 1);
      timer = window.setTimeout(applyThemeForLocalTime, nextBoundary.getTime() - Date.now());
    };
    applyThemeForLocalTime();
    return () => { if (timer !== undefined) window.clearTimeout(timer); };
  }, []);

  function authenticate(next: AuthSession): void {
    storeSession(next);
    setSession(next);
  }

  async function logout(): Promise<void> {
    try {
      await props.api.logout();
    } finally {
      window.localStorage.removeItem(sessionStorageKey);
      setSession(null);
    }
  }

  return (
    <>
      <SyntheticSmokeModeBanner enabled={import.meta.env.VITE_SCS_SMOKE_TEST_MODE === "true"} />
      {session === null ? null : (
        <header>
          <nav aria-label="主导航">
            <Link to="/conversations">会话工作台</Link>
            <Link to="/sessions">Session</Link>
            {session.user.role === "ENTERPRISE_ADMIN"
              ? <><Link to="/admin/users">用户管理</Link><Link to="/governance">数据治理</Link></>
              : null}
            <div className="app-nav-actions">
              <CurrentAccount user={session.user} />
              <button onClick={() => { void logout(); }} type="button">退出</button>
            </div>
          </nav>
        </header>
      )}
      <Routes>
        <Route
          element={session === null
            ? <LoginPage api={props.api} onAuthenticated={authenticate} tenantId={import.meta.env.VITE_LOGIN_TENANT_ID} />
            : <Navigate replace to="/conversations" />}
          path="/login"
        />
        <Route
          element={
            <ProtectedRoute allowedRoles={tenantRoles} session={session}>
              <ConversationsPage
                api={props.api}
                currentUserId={session?.user.id ?? ""}
                role={session?.user.role ?? "AUDITOR"}
                sessionToken={session?.sessionToken ?? ""}
              />
            </ProtectedRoute>
          }
          path="/conversations"
        />
        <Route
          element={
            <ProtectedRoute allowedRoles={tenantRoles} session={session}>
              <SessionPage
                api={props.api}
                canManage={session?.user.role === "ENTERPRISE_ADMIN"}
                canBindSelf={session?.user.role === "COLLECTOR"}
                currentUserId={session?.user.id ?? null}
              />
            </ProtectedRoute>
          }
          path="/sessions"
        />
        <Route
          element={
            <ProtectedRoute allowedRoles={["ENTERPRISE_ADMIN"]} session={session}>
              <AdminUsersPage api={props.api} />
            </ProtectedRoute>
          }
          path="/admin/users"
        />
        <Route
          element={
            <ProtectedRoute allowedRoles={["ENTERPRISE_ADMIN"]} session={session}>
              <GovernancePage api={props.api} />
            </ProtectedRoute>
          }
          path="/governance"
        />
        <Route element={<Navigate replace to="/conversations" />} path="*" />
      </Routes>
    </>
  );
}
